import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {forkJoin, merge, Subject} from 'rxjs';
import {take, takeUntil} from 'rxjs/operators';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {CaseHospitalDTO} from '../../../../models/case-hospital/CaseHospitalDTO';
import {CaseManagerLinkDTO} from '../../../../models/CaseManagerLinkDTO';
import {CaseStatusLinkDTO} from '../../../../models/CaseStatusLinkDTO';
import {UtilService} from '../../../../../common/services/util.service';
import {NameValueDTO} from '../../../../../common/models/NameValueDTO';
import {SharedService} from '../../../../../common/services/shared-service';
import {SharedFormService} from '../../../../../common/services/shared-form-service';
import {MatButton} from '@angular/material/button';
import {
  ConfirmationDialogComponent
} from '../../../../../common/dialogs/confirmation-dialog/confirmation-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {DisplayStrings} from '../../../../../common/constants/DisplayStrings';
import {CaseStatusDTO} from '../../../../models/CaseStatusDTO';
import {CaseManagerDTO} from '../../../../models/CaseManagerDTO';
import {CaseStatus, CaseType} from '../../../../types';
import {MatRadioChange} from '@angular/material/radio';
import {LookupService} from '../../../../../common/services/lookup.service';
import {LookupValue} from '../../../../types/lookup-value.enum';
import {CaseDischargeDialogComponent} from '../../../dialogs/case-discharge-dialog/case-discharge-dialog.component';
import {CaseFuneralDTO} from '../../../../models/CaseFuneralDTO';
import {KeycloakService} from 'keycloak-angular';
import {UserAuthorisationService} from 'src/app/modules/common/services/user-authorisation.service';

// Status Step
enum StatusType {
  YES = 'yes',
  NO = 'no'
}

enum StatusStepRadioGroupName {
  CASE_STATUS_RADIO_GROUP = 'caseStatusRadioGroup',
  CASE_MANAGER_RADIO_GROUP = 'caseManagerRadioGroup'
}

enum Controls {
  STATUS_OPTIONS = 'statusOptions',
  MANAGER_OPTIONS = 'managerOptions',
  CASE_STATUS = 'caseStatus',
  CASE_MANAGER = 'caseManager',
  CASE_STATUS_START_DATE = 'caseStatusStartDateFormControl',
  CASE_MANAGER_START_DATE = 'caseManagerStartDateFormControl',
  CASE_STATUS_MIN_START_DATE = 'caseStatusMinStartDateFormControl',
  CASE_MANAGER_MIN_START_DATE = 'caseManagerMinStartDateStartDateFormControl',
}

@Component({
  selector: 'app-case-status-step',
  templateUrl: './case-status-step.component.html',
  styleUrls: [
    '../../../../../../../styles/global/_add-view-edit-styles.scss',
    './case-status-step.component.scss']
})
export class CaseStatusStepComponent implements OnChanges, OnInit {
  readonly statusType = StatusType;
  readonly controls = Controls;
  readonly caseType = CaseType;
  readonly caseStatus = CaseStatus;
  readonly destroyed$ = new Subject();
  readonly displayStrings = DisplayStrings;
  readonly lookupValue = LookupValue;

  @Input() type: string;
  @Input() data: CaseFuneralDTO | CaseHospitalDTO;
  @Input() formGroup: FormGroup;
  @Input() isEditMode: boolean;

  @Output() updateForm: EventEmitter<FormGroup> = new EventEmitter();

  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onSave = new EventEmitter<any>();

  // This option is selected from Discharge step:
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onDischargeType: EventEmitter<string> = new EventEmitter();

  caseStatusLookupValues: NameValueDTO[];
  caseManagerLookupValues: NameValueDTO[];
  caseStatusLinkDTOHistoryList: CaseStatusLinkDTO[] = [];
  caseManagerLinkDTOHistoryList: CaseManagerLinkDTO[] = [];
  statusStepRadioGroupName = StatusStepRadioGroupName;
  isCaseStatusOrMangerOptionEnabled: boolean;
  isCaseStatusAndCaseManagerEntryExists = false;

  private selectedCaseManager: NameValueDTO;
  private selectedCaseStatus: NameValueDTO;
  private loggedInUserName: string;

  // To Show/Hide button for Case-Discharge-Step-Dialog and for Medical Case:
  private processingClass: string;

  @ViewChild('finalStepBackButton', {static: false}) private finalStepBackButton: MatButton;
  @ViewChild('finalStepClearButton', {static: false}) private finalStepClearButton: MatButton;
  @ViewChild('finalStepFinishButton', {static: false}) private finalStepFinishButton: MatButton;

  constructor(private dialog: MatDialog,
              private formBuilder: FormBuilder,
              private lookupService: LookupService,
              private utilService: UtilService,
              private sharedService: SharedService,
              private sharedFormService: SharedFormService,
              private keycloakService: KeycloakService,
              private userAuthorisationService: UserAuthorisationService) {
  }

  get isAuthorised(): boolean {
    return this.userAuthorisationService.hasRoleClaimsAssessor();
  }

  get isCaseMedical(): boolean {
    return this.processingClass === CaseType.Medical;
  }

  get isFormValid(): boolean {
    return this.formGroup.valid;
  }

  get isStepValid(): boolean {
    return this.isCaseStatusAndCaseManagerEntryExists && !this.isCaseStatusOrMangerOptionEnabled;
  }

  get isCaseDateLessThanTodayDate(): boolean {
    return this.isDateLessThanEqual();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.type) {
      this.type = changes.type.currentValue;
    }
    if (changes.isEditMode) {
      this.isEditMode = changes.isEditMode.currentValue;
    }
    if (changes.formGroup) {
      this.formGroup = changes.formGroup.currentValue;
    }
    if (changes.data && changes.data.currentValue) {
      this.data = changes.data.currentValue;
      this.setFormsForEdit(this.data);
    }
  }

  ngOnInit(): void {
    this.initSubscriptions();
    this.initHelpInfo();
    this.initLookUpValues();
    this.initStatusStepFormControls();
  }

  initSubscriptions() {
    this.sharedService.getLoggedInProfile$()
      .pipe(
        take(1)
      )
      .subscribe(() => {
        // Note: Won't be using profile obj instead invoke `getLoggedInUserName` as username that is already set in service.
        this.loggedInUserName = this.sharedService.getLoggedInUserName();
      });
  }

  initStatusStepFormControls() {
    this.formGroup = this.formBuilder.group({
      [this.controls.STATUS_OPTIONS]: !this.isEditMode ? [this.statusType.YES] : [this.statusType.NO],
      [this.controls.MANAGER_OPTIONS]: !this.isEditMode ? [this.statusType.YES] : [this.statusType.NO],
      [this.controls.CASE_STATUS]: new FormControl(null, !this.isEditMode ? Validators.required : []),
      [this.controls.CASE_MANAGER]: new FormControl(null, !this.isEditMode ? Validators.required : []),
      [this.controls.CASE_STATUS_START_DATE]: new FormControl(new Date(), !this.isEditMode ? Validators.required : []),
      [this.controls.CASE_MANAGER_START_DATE]: new FormControl(new Date(), !this.isEditMode ? Validators.required : []),
      [this.controls.CASE_STATUS_MIN_START_DATE]: new FormControl(null),
      [this.controls.CASE_MANAGER_MIN_START_DATE]: new FormControl(null)
    });

    // Notify Updated Reference of FormGroup to parent:
    this.updateForm.next(this.formGroup);

    // Emit Updated FormGroup controls
    merge(
      this.formGroup.get(this.controls.STATUS_OPTIONS).valueChanges,
      this.formGroup.get(this.controls.MANAGER_OPTIONS).valueChanges,
      this.formGroup.get(this.controls.CASE_STATUS).valueChanges,
      this.formGroup.get(this.controls.CASE_MANAGER).valueChanges,
      this.formGroup.get(this.controls.CASE_STATUS_START_DATE).valueChanges,
      this.formGroup.get(this.controls.CASE_MANAGER_START_DATE).valueChanges,
      this.formGroup.get(this.controls.CASE_STATUS_MIN_START_DATE).valueChanges,
      this.formGroup.get(this.controls.CASE_MANAGER_MIN_START_DATE).valueChanges
    ).pipe(
      takeUntil(this.destroyed$)
    )
      .subscribe(_ => {
        // Note: Emit Always:
        // Case Add/Edit select or reset values in both case
        this.updateForm.next(this.formGroup);
      });
  }

  initHelpInfo() {
    // Todo: Future Handle Funeral Case
    if (this.type === this.caseType.Medical) {
      this.getHelpInfo('Hospital Case');
    }
  }

  resetStatusFormGroup() {
    this.formGroup.reset();

    // Note: reset of form resets value and "yes" default value is not selected therefore explicitly reset to init values:
    this.initStatusStepFormControls();
  }

  getHelpInfo(componentName: string) {
    this.sharedService.getHelpInfo(componentName);
  }

  getInfoTooltip(inputName: string): string {
    return this.sharedService.getInfoTooltip(inputName);
  }

  onDeleteHistory(type: any) {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '40vw',
      data: `Are you sure you want to delete ${type.historyDisplayString} history?`,
      panelClass: 'white-space-none'
    });

    dialogRef.afterClosed()
      .pipe(
        take(1)
      )
      .subscribe((isConfirmed) => {
        if (isConfirmed) {
          this.onDeleteHistoryConfirmed(type);
        }
        else {
          dialogRef.close();
        }
      });
  }

  onDeleteHistoryConfirmed(type: any) {
    if (type.caseStatusDTO) {
      const index = this.caseStatusLinkDTOHistoryList
        .findIndex((item: CaseStatusLinkDTO) => {
          return item.caseStatusDTO.caseStatusId === type.caseStatusDTO.caseStatusId;
        });
      this.caseStatusLinkDTOHistoryList.splice(index, 1);
      this.setMinDateCaseStatusDatepicker();

    }
    else if (type?.caseManagerDTO) {
      const index = this.caseManagerLinkDTOHistoryList
        .findIndex((item: CaseManagerLinkDTO) => {
          return item.caseManagerDTO.caseManagerId === type.caseManagerDTO.caseManagerId;
        });
      this.caseManagerLinkDTOHistoryList.splice(index, 1);
      this.setMinDateCaseManagerDatepicker();
    }

    this.isCaseStatusAndCaseManagerExists();
    this.clearMinDateDatepicker();
  }

  onCaseStatusSelected(caseStatus: NameValueDTO) {
    if (caseStatus) {
      this.selectedCaseStatus = caseStatus;
    }
    else {
      this.selectedCaseStatus = null;
    }
  }

  onCaseManagerSelected(caseManager: NameValueDTO) {
    if (caseManager) {
      this.selectedCaseManager = caseManager;
    }
    else {
      this.selectedCaseManager = null;
    }
  }

  onUpdateCaseStatusHistory() {
    if (this.formGroup.get(this.controls.CASE_STATUS).value && this.formGroup.get(this.controls.CASE_STATUS_START_DATE).value) {
      const newCaseStatusHistory = this.getCaseStatusLinkDTOPayload();
      let updatedStatusHistoryList: CaseStatusLinkDTO[] = [];

      // Note: Ideal case only 1 entry should be provided but
      // In case user tries to add more than 1 "case status history" then handle accordingly
      if (this.isCaseStatusHistoryExist()) {
        const currentStatus = this.formGroup.get(this.controls.CASE_STATUS).value;
        const firstStatus = this.caseStatusLinkDTOHistoryList[0].caseStatusDTO; // already sorted
        const existingStatus = firstStatus.statusName;

        if (existingStatus === currentStatus) {
          this.sharedService.openSnackBar(`The case’s status is already set to ${currentStatus}`, 'Dismiss');
          return;
        }
        else {
          updatedStatusHistoryList = [...this.caseStatusLinkDTOHistoryList, {...newCaseStatusHistory}];
        }
      }
      else {
        updatedStatusHistoryList.push(newCaseStatusHistory);
      }
      updatedStatusHistoryList = this.sortByColumn(updatedStatusHistoryList, 'startDate');
      updatedStatusHistoryList = this.updateEndDateOnUpdate(updatedStatusHistoryList);
      updatedStatusHistoryList = this.getCaseStatusHistoryDisplayString(updatedStatusHistoryList);
      this.caseStatusLinkDTOHistoryList = updatedStatusHistoryList;
      this.setMinDateCaseStatusDatepicker();

      // Disable form till at least 1 entry exists:
      this.isCaseStatusAndCaseManagerExists();

      // Patch to "Default" values
      this.formGroup.patchValue({
        [this.controls.STATUS_OPTIONS]: this.statusType.NO,
        [this.controls.CASE_STATUS]: null,
        [this.controls.CASE_STATUS_START_DATE]: null
      });

      this.clearCaseStatusHistoryValidator();
    }
    else {
      this.sharedService.openSnackBar(this.displayStrings.generic.requiredFields, 'Dismiss');
    }
  }

  onUpdateCaseManagerHistory() {
    if (this.formGroup.get(this.controls.CASE_MANAGER).value && this.formGroup.get(this.controls.CASE_MANAGER_START_DATE).value) {
      const newCaseManagerHistory = this.getCaseManagerLinkDTOPayload();
      let updatedManagerHistoryList: CaseManagerLinkDTO[] = [];

      // Note: Ideal case only 1 entry should be provided but
      // In case user tries to add more than 1 "case manager history" then handle accordingly
      if (this.isCaseManagerHistoryExist()) {
        const currentManager = this.formGroup.get(this.controls.CASE_MANAGER).value;
        const firstManager = this.caseManagerLinkDTOHistoryList[0].caseManagerDTO; // already sorted
        const existingManagerName = `${firstManager.firstName} ${firstManager.lastName}`;
        if (existingManagerName === currentManager) {
          this.sharedService.openSnackBar(`The case’s manager is already set to ${currentManager}`, 'Dismiss');
          return;
        }
        else {
          updatedManagerHistoryList = [...this.caseManagerLinkDTOHistoryList, {...newCaseManagerHistory}];
        }
      }
      else {
        updatedManagerHistoryList.push(newCaseManagerHistory);
      }

      updatedManagerHistoryList = this.sortByColumn(updatedManagerHistoryList, 'startDate');
      updatedManagerHistoryList = this.updateEndDateOnUpdate(updatedManagerHistoryList);
      updatedManagerHistoryList = this.getCaseManagerHistoryDisplayString(updatedManagerHistoryList);
      this.caseManagerLinkDTOHistoryList = updatedManagerHistoryList;
      this.setMinDateCaseManagerDatepicker();

      // Disable form till at least 1 entry exists:
      this.isCaseStatusAndCaseManagerExists();

      // Reset Value:
      // Patch to "Default" values
      this.formGroup.patchValue({
        [this.controls.MANAGER_OPTIONS]: this.statusType.NO,
        [this.controls.CASE_MANAGER]: null,
        [this.controls.CASE_MANAGER_START_DATE]: null
      });
      this.clearCaseManagerHistoryValidator();
    }
    else {
      this.sharedService.openSnackBar(this.displayStrings.generic.requiredFields, 'Dismiss');
    }
  }

  onUpdateStatusRadioOption($event: MatRadioChange) {
    if ($event.value === this.statusType.YES) {
      this.setCaseStatusHistoryValidator();
    }
    else {
      this.clearCaseStatusHistoryValidator();
      // Reset value in case option is disabled:
      this.setMinDateCaseStatusDatepicker();
      this.formGroup.patchValue({
        [this.controls.CASE_STATUS]: null,
      });
    }
  }

  onUpdateManagerRadioOption($event: MatRadioChange) {
    if ($event.value === this.statusType.YES) {
      this.setCaseManagerHistoryValidator();
    }
    else {
      this.clearCaseManagerHistoryValidator();
      this.setMinDateCaseStatusDatepicker();
      // Reset value in case option is disabled:
      this.formGroup.patchValue({
        [this.controls.CASE_MANAGER]: null,
      });
    }
  }

  initLookUpValues() {
    forkJoin(
      this.lookupService.getListOfLookupValues(this.lookupValue.CaseStatus),
      this.lookupService.getListOfLookupValues(this.lookupValue.CaseManager)
    )
      .pipe(
        take(1)
      )
      .subscribe(
        (responses: any) => {
          this.caseStatusLookupValues = responses[0];
          this.caseManagerLookupValues = responses[1];
        },
        (error) => {
          console.log(error);
        });
  }

  // ------------------------------
  // Useful for Case Medical
  // ------------------------------

  onDischarge() {
    // Open the dialog with a NULL narrative for a new narrative, or the existing narrative to edit a narrative
    const dialogRef = this.dialog.open(CaseDischargeDialogComponent, {
      width: '37vw'
    });

    dialogRef.afterClosed().subscribe((dischargeType: string) => {
      if (dischargeType) {
        this.onDischargeType.next(dischargeType);
      }
    });
  }

  saveCaseHospitalDTO() {
    this.onSave.next(true);
  }

  getPayload() {
    return {
      caseStatuses: this.caseStatusLinkDTOHistoryList ? this.caseStatusLinkDTOHistoryList : null,
      caseManagers: this.caseManagerLinkDTOHistoryList ? this.caseManagerLinkDTOHistoryList : null,
    };
  }

  private setCaseStatusHistoryValidator() {
    this.sharedFormService.setValidator(this.formGroup, this.controls.CASE_STATUS, [Validators.required]);
    this.sharedFormService.setValidator(this.formGroup, this.controls.CASE_STATUS_START_DATE, [Validators.required]);
    this.isCaseStatusOrMangerOptionEnabled = true;
  }

  private setCaseManagerHistoryValidator() {
    this.sharedFormService.setValidator(this.formGroup, this.controls.CASE_MANAGER, [Validators.required]);
    this.sharedFormService.setValidator(this.formGroup, this.controls.CASE_MANAGER_START_DATE, [Validators.required]);
    this.isCaseStatusOrMangerOptionEnabled = true;
  }

  private getCaseStatusLinkDTOPayload(): CaseStatusLinkDTO {
    let payload: CaseStatusLinkDTO;
    payload = {
      caseStatusDTO: this.getCaseStatusDTOPayload(),
      startDate: new Date(this.formGroup.get(this.controls.CASE_STATUS_START_DATE).value),
      endDate: null,
      created: this.isEditMode ? null : new Date(),
      createdBy: this.isEditMode ? null : this.loggedInUserName,
      updated: this.isEditMode ? new Date() : null,
      updatedBy: this.isEditMode ? this.loggedInUserName : null,
      tenantId: null
    } as CaseStatusLinkDTO;

    return payload;
  }

  private getCaseStatusDTOPayload(): CaseStatusDTO {
    let payload: CaseStatusDTO;
    payload = {
      caseStatusId: this.selectedCaseStatus.value,
      statusName: this.formGroup.get(this.controls.CASE_STATUS).value,
      created: this.isEditMode ? null : new Date(),
      createdBy: this.isEditMode ? null : this.loggedInUserName,
      updated: this.isEditMode ? new Date() : null,
      updatedBy: this.isEditMode ? this.loggedInUserName : null,
      tenantId: null,
    } as CaseStatusDTO;
    return payload;
  }

  private getCaseManagerLinkDTOPayload(): CaseManagerLinkDTO {
    let payload: CaseManagerLinkDTO;
    payload = {
      caseManagerDTO: this.getCaseManagerDTOPayload(),
      startDate: new Date(this.formGroup.get(this.controls.CASE_MANAGER_START_DATE).value),
      endDate: null,
      created: this.isEditMode ? null : new Date(),
      createdBy: this.isEditMode ? null : this.loggedInUserName,
      updated: this.isEditMode ? new Date() : null,
      updatedBy: this.isEditMode ? this.loggedInUserName : null,
      tenantId: null,
    } as CaseManagerLinkDTO;

    return payload;
  }

  private getCaseManagerDTOPayload(): CaseManagerDTO {
    const splitFullName = this.formGroup.get(this.controls.CASE_MANAGER).value.split(' ');
    let payload: CaseManagerDTO;
    payload = {
      caseManagerId: this.selectedCaseManager.value,
      firstName: splitFullName[0],
      lastName: splitFullName[1],
      designationId: null,
      created: this.isEditMode ? null : new Date(),
      createdBy: this.isEditMode ? null : this.loggedInUserName,
      updated: this.isEditMode ? new Date() : null,
      updatedBy: this.isEditMode ? this.loggedInUserName : null,
      tenantId: null,
    } as CaseManagerDTO;
    return payload;
  }

  private clearMinDateDatepicker() {
    if (!this.isCaseStatusHistoryExist()) {
      this.formGroup.patchValue({
        [this.controls.CASE_STATUS_MIN_START_DATE]: null
      });
    }
    else if (!this.isCaseManagerHistoryExist()) {
      this.formGroup.patchValue({
        [this.controls.CASE_MANAGER_MIN_START_DATE]: null
      });
    }
  }

  private setFormsForEdit(data: CaseFuneralDTO | CaseHospitalDTO) {
    this.setCaseManagerHistoryEdit(data);
    this.setCaseStatusHistoryEdit(data);

    // To Show/Hide button for Case-Discharge-Step-Dialog and for Medical Case:
    this.processingClass = data.caseDTO.processingClass.name;
  }

  private setCaseStatusHistoryEdit(data: CaseFuneralDTO | CaseHospitalDTO) {
    const {caseStatuses} = data;
    if (caseStatuses && caseStatuses.length > 0) {
      let list;
      list = caseStatuses.map((caseStatus: CaseStatusLinkDTO) => {
        caseStatus.updated = new Date();
        caseStatus.updatedBy = this.loggedInUserName;
        caseStatus.caseStatusDTO.updated = new Date();
        caseStatus.caseStatusDTO.updatedBy = this.loggedInUserName;
        return caseStatus;
      });
      list = this.sortByColumn(list, 'startDate');
      list = this.updateEndDateOnUpdate(list);
      list = this.getCaseStatusHistoryDisplayString(list);
      this.caseStatusLinkDTOHistoryList = list;
      this.setMinDateCaseStatusDatepicker();
      this.isCaseStatusAndCaseManagerExists();
      this.clearCaseStatusHistoryValidator();
    }
    else {
      this.caseStatusLinkDTOHistoryList = [];
    }
  }

  private setCaseManagerHistoryEdit(data: CaseFuneralDTO | CaseHospitalDTO) {
    const {caseManagers} = data;
    if (caseManagers && caseManagers.length > 0) {
      let list;
      list = caseManagers.map((caseManager: CaseManagerLinkDTO) => {
        caseManager.updated = new Date();
        caseManager.updatedBy = this.loggedInUserName;
        caseManager.caseManagerDTO.updated = new Date();
        caseManager.caseManagerDTO.updatedBy = this.loggedInUserName;
        return caseManager;
      });
      list = this.sortByColumn(list, 'startDate');
      list = this.updateEndDateOnUpdate(list);
      list = this.getCaseManagerHistoryDisplayString(list);
      this.caseManagerLinkDTOHistoryList = list;
      this.setMinDateCaseManagerDatepicker();
      this.isCaseStatusAndCaseManagerExists();
      this.clearCaseManagerHistoryValidator();
    }
    else {
      this.caseManagerLinkDTOHistoryList = [];
    }
  }

  private sortByColumn(list, columnName: string) {
    return list.sort((firstElement, secondElement) => {
      return new Date(secondElement[columnName]).getTime() - new Date(firstElement[columnName]).getTime();
    });
  }

  private updateEndDateOnUpdate(list: CaseStatusLinkDTO[] | CaseManagerLinkDTO[]) {
    const newList: any[] = JSON.parse(JSON.stringify(list));
    if (newList.length > 1) {
      for (let index = 0; index < newList.length - 1; index++) {
        const endDateToSet = new Date(newList[index].startDate).getDate() - 1;
        newList[index + 1].endDate = new Date(new Date(newList[index].startDate).setDate(endDateToSet));
      }
    }
    return newList;
  }

  private getCaseStatusHistoryDisplayString(list: CaseStatusLinkDTO[]) {
    const newList: any[] = JSON.parse(JSON.stringify(list));
    newList.map((item: CaseStatusLinkDTO) => {
      item.historyDisplayString = `
        ${item.caseStatusDTO.statusName}: ${this.utilService.dateFormat(item.startDate)} -
        ${item.endDate ? this.utilService.dateFormat(item.endDate) : ''}`.trim();
      return item;
    });
    return newList;
  }

  private getCaseManagerHistoryDisplayString(list: CaseManagerLinkDTO[]) {
    const newList: any[] = JSON.parse(JSON.stringify(list));
    newList.map((item: CaseManagerLinkDTO) => {
      item.historyDisplayString = `
        ${item.caseManagerDTO.firstName} ${item.caseManagerDTO.lastName}: ${this.utilService.dateFormat(item.startDate)} -
        ${item.endDate ? this.utilService.dateFormat(item.endDate) : ''}`.trim();
      return item;
    });
    return newList;
  }

  private setMinDateCaseStatusDatepicker() {
    // Order is important as it should be after list is sorted based on `startDate`
    // The "minDate" property on the date picker should be "1" day greater than the latest "status" startDate
    let latestDateToSet;
    if (this.isCaseStatusHistoryExist()) {
      const caseStatusItem = this.caseStatusLinkDTOHistoryList[0];
      latestDateToSet = this.getLatestStatusDateToSet(caseStatusItem);
    }
    else {
      latestDateToSet = new Date();
    }
    this.formGroup.patchValue({
      [this.controls.CASE_STATUS_START_DATE]: latestDateToSet, // default to T+1, the same date as the minimum date.
      [this.controls.CASE_STATUS_MIN_START_DATE]: latestDateToSet
    });
  }

  private setMinDateCaseManagerDatepicker() {
    // Order is important as it should be after list is sorted based on `startDate`
    // The "minDate" property on the date picker should be "1" day greater than the latest "status" startDate
    let latestDateToSet;

    if (this.isCaseManagerHistoryExist()) {
      const caseManagerItem = this.caseManagerLinkDTOHistoryList[0];
      latestDateToSet = this.getLatestStatusDateToSet(caseManagerItem);
    }
    else {
      latestDateToSet = new Date();
    }
    this.formGroup.patchValue({
      [this.controls.CASE_MANAGER_START_DATE]: latestDateToSet, // default to T+1, the same date as the minimum date.
      [this.controls.CASE_MANAGER_MIN_START_DATE]: latestDateToSet
    });
  }

  private getLatestStatusDateToSet(caseItem: CaseStatusLinkDTO | CaseManagerLinkDTO) {
    /*
     Note: Edge Case
     In case of multiple status once

     statusName: "startDate - "         =>  TOP status is "REMOVED"
     statusName: "startDate - endDate"  => SECOND status

     Important! Calendar "minDate" should be endDate + 1 from -> SECOND status
    */

    let latestStartDateToSet;
    if (caseItem.startDate && caseItem.endDate) {
      latestStartDateToSet = new Date(new Date(caseItem.endDate)
        .setDate(new Date(caseItem.endDate).getDate() + 1));
    }
    else {
      latestStartDateToSet = new Date(new Date(caseItem.startDate)
        .setDate(new Date(caseItem.startDate).getDate() + 1));
    }

    return latestStartDateToSet;
  }

  private clearCaseStatusHistoryValidator() {
    this.sharedFormService.clearValidator(this.formGroup, this.controls.CASE_STATUS);
    this.sharedFormService.clearValidator(this.formGroup, this.controls.CASE_STATUS_START_DATE);
    this.isCaseStatusOrMangerOptionEnabled = false;
  }

  private clearCaseManagerHistoryValidator() {
    this.sharedFormService.clearValidator(this.formGroup, this.controls.CASE_MANAGER);
    this.sharedFormService.clearValidator(this.formGroup, this.controls.CASE_MANAGER_START_DATE);
    this.isCaseStatusOrMangerOptionEnabled = false;
  }

  private isCaseStatusHistoryExist() {
    return !!(this.caseStatusLinkDTOHistoryList && this.caseStatusLinkDTOHistoryList.length > 0);
  }

  private isCaseManagerHistoryExist() {
    return !!(this.caseManagerLinkDTOHistoryList && this.caseManagerLinkDTOHistoryList.length > 0);
  }

  private isCaseStatusAndCaseManagerExists() {
    this.isCaseStatusAndCaseManagerEntryExists = (this.isCaseStatusHistoryExist() && this.isCaseManagerHistoryExist());
  }

  private isDateLessThanEqual(): boolean {
    if (this.caseStatusLinkDTOHistoryList && this.caseStatusLinkDTOHistoryList.length > 0) {
      const todayDate = new Date();
      const topStatusStartDate = this.caseStatusLinkDTOHistoryList[0]?.startDate;
      const topStatusEndDate = this.caseStatusLinkDTOHistoryList[0]?.endDate;
      const topStatusStartDateLessThanEqualToTodayDate = new Date(topStatusStartDate).getTime() <= todayDate.getTime();
      const topStatusEndDatGreaterThanOrNull = new Date(topStatusEndDate).getTime() > todayDate.getTime() || topStatusEndDate === null;
      return topStatusStartDateLessThanEqualToTodayDate && topStatusEndDatGreaterThanOrNull;
    }
    else {
      return false;
    }
  }
}
